import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useMeta } from "@/hooks/useMeta";
import PageHeader from "@/components/common/PageHeader";
import StatusBadge from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Loader2, ShieldCheck, ShieldAlert, ShieldX, Search, ArrowRight, Eye } from "lucide-react";

const Field = ({ label, children, required }) => (
  <div>
    <Label className="text-sm">{label}{required && <span className="text-rose-500"> *</span>}</Label>
    <div className="mt-1.5">{children}</div>
  </div>
);

export default function AddLead() {
  const { user } = useAuth();
  const meta = useMeta();
  const navigate = useNavigate();
  const isManager = ["manager", "super_admin"].includes(user?.role);

  const [step, setStep] = useState(1);
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState(null);
  const [override, setOverride] = useState(false);
  const [saving, setSaving] = useState(false);

  const [search, setSearch] = useState({ mobile: "", email: "", doctor_name: "", org_name: "", city: "", website: "", address: "", gstin: "" });
  const [form, setForm] = useState({
    lead_type: "Clinic", org_name: "", contact_person: "", mobile: "", whatsapp: "", email: "",
    address: "", city: "", website: "", source: "Field Visit", requirement: "",
    num_doctors: "", num_locations: "", estimated_value: "", notes: "", private_notes: "",
    next_action: "", next_action_date: "",
  });

  const setS = (k, v) => setSearch((p) => ({ ...p, [k]: v }));
  const setF = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const hasSearchInput = Object.values(search).some((v) => (v || "").trim());

  const runCheck = async () => {
    if (!hasSearchInput) return toast.error("Enter at least one field to search");
    setChecking(true);
    setResult(null);
    setOverride(false);
    try {
      const { data } = await api.post("/leads/duplicate-check", search);
      setResult(data);
      if (data.status === "none") toast.success("No duplicates found — you can continue");
      else if (data.status === "possible") toast.warning(`${data.possible_count} possible match(es) found`);
      else toast.error("Exact duplicate exists — cannot create");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setChecking(false);
    }
  };

  const canContinue = result && (result.status === "none" || (result.status === "possible" && (isManager ? override : false)));

  const goToDetails = () => {
    setForm((p) => ({
      ...p,
      org_name: search.org_name || p.org_name,
      contact_person: search.doctor_name || p.contact_person,
      mobile: search.mobile || p.mobile,
      email: search.email || p.email,
      city: search.city || p.city,
      website: search.website || p.website,
      address: search.address || p.address,
    }));
    setStep(2);
  };

  const submit = async () => {
    if (!form.org_name.trim()) return toast.error("Organization name is required");
    if (!form.next_action.trim()) return toast.error("An active lead requires a next action");
    setSaving(true);
    try {
      const payload = {
        ...form,
        duplicate_check_id: result.check_id,
        override_approved: override,
        num_doctors: form.num_doctors ? Number(form.num_doctors) : null,
        num_locations: form.num_locations ? Number(form.num_locations) : null,
        estimated_value: form.estimated_value ? Number(form.estimated_value) : null,
      };
      const { data } = await api.post("/leads", payload);
      toast.success("Lead created");
      navigate(`/leads/${data.id}`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const StepBadge = ({ n, label, active, done }) => (
    <div className="flex items-center gap-2">
      <div className={`grid place-items-center h-8 w-8 rounded-full text-sm font-bold ${active ? "bg-teal text-white" : done ? "bg-emerald-500 text-white" : "bg-slate-200 text-slate-500"}`}>{n}</div>
      <span className={`text-sm font-medium ${active ? "text-slate-900" : "text-slate-400"}`}>{label}</span>
    </div>
  );

  return (
    <div className="space-y-6 max-w-3xl mx-auto">
      <PageHeader title="Add New Lead" subtitle="Check for duplicates before creating a lead." testid="add-lead-header" />

      <div className="flex items-center gap-4">
        <StepBadge n={1} label="Duplicate Check" active={step === 1} done={step > 1} />
        <div className="flex-1 h-px bg-slate-200" />
        <StepBadge n={2} label="Lead Details" active={step === 2} done={false} />
      </div>

      {step === 1 && (
        <div className="bg-white border border-slate-200/80 rounded-xl shadow-sm p-6 space-y-5" data-testid="dup-check-step">
          <p className="text-sm text-slate-500">Search existing records first. The <b>Continue</b> button unlocks after a duplicate check.</p>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Mobile number"><Input value={search.mobile} onChange={(e) => setS("mobile", e.target.value)} data-testid="dup-mobile" placeholder="+91…" /></Field>
            <Field label="Email"><Input value={search.email} onChange={(e) => setS("email", e.target.value)} data-testid="dup-email" /></Field>
            <Field label="Doctor / contact name"><Input value={search.doctor_name} onChange={(e) => setS("doctor_name", e.target.value)} data-testid="dup-doctor" /></Field>
            <Field label="Clinic / hospital name"><Input value={search.org_name} onChange={(e) => setS("org_name", e.target.value)} data-testid="dup-org" /></Field>
            <Field label="City"><Input value={search.city} onChange={(e) => setS("city", e.target.value)} data-testid="dup-city" /></Field>
            <Field label="Website"><Input value={search.website} onChange={(e) => setS("website", e.target.value)} data-testid="dup-website" /></Field>
            <Field label="Address"><Input value={search.address} onChange={(e) => setS("address", e.target.value)} data-testid="dup-address" /></Field>
            <Field label="GSTIN / Reg. no."><Input value={search.gstin} onChange={(e) => setS("gstin", e.target.value)} data-testid="dup-gstin" /></Field>
          </div>

          <Button onClick={runCheck} disabled={checking || !hasSearchInput} className="bg-navy hover:bg-navy-700 h-11" data-testid="run-dup-check-btn">
            {checking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Check for duplicates
          </Button>

          {result && (
            <div className="space-y-3" data-testid="dup-result">
              {result.status === "none" && (
                <div className="flex items-start gap-3 rounded-lg bg-emerald-50 border border-emerald-200 p-4 text-sm text-emerald-800">
                  <ShieldCheck className="h-5 w-5 shrink-0" /><div><b>No duplicates found.</b> You can safely create this lead.</div>
                </div>
              )}
              {result.status === "possible" && (
                <div className="rounded-lg bg-amber-50 border border-amber-200 p-4 text-sm text-amber-800 space-y-3">
                  <div className="flex items-start gap-3"><ShieldAlert className="h-5 w-5 shrink-0" /><div><b>Possible duplicate(s) detected.</b> Manager approval is required before continuing.</div></div>
                  {result.matches.map((m) => (
                    <div key={m.id} className="flex items-center justify-between bg-white rounded-lg border border-amber-200 px-3 py-2">
                      <div className="min-w-0"><div className="font-semibold text-slate-800 truncate">{m.name}</div><div className="text-xs text-slate-500">{m.reasons?.join(", ")}</div></div>
                      <span className="text-xs font-bold text-amber-700">{m.similarity}%</span>
                    </div>
                  ))}
                  {isManager ? (
                    <label className="flex items-center gap-2 pt-1"><Checkbox checked={override} onCheckedChange={setOverride} data-testid="dup-override-check" /><span>I've reviewed these and approve creating a new lead.</span></label>
                  ) : (
                    <div className="text-xs text-amber-700 pt-1">A manager must review this. Ask your manager to create it or reassign after approval.</div>
                  )}
                </div>
              )}
              {result.status === "exact" && (
                <div className="rounded-lg bg-rose-50 border border-rose-200 p-4 text-sm text-rose-800 space-y-3">
                  <div className="flex items-start gap-3"><ShieldX className="h-5 w-5 shrink-0" /><div><b>Exact duplicate exists.</b> A new lead cannot be created.</div></div>
                  {result.matches.map((m) => (
                    <div key={m.id} className="flex items-center justify-between bg-white rounded-lg border border-rose-200 px-3 py-2">
                      <div className="min-w-0">
                        <div className="font-semibold text-slate-800 truncate">{m.name}</div>
                        <div className="text-xs text-slate-500">ID {m.id.slice(0, 8)} · {m.status} · Owner: {m.owner_name || "Restricted"}</div>
                      </div>
                      {m.owner_id != null && (
                        <Button size="sm" variant="outline" onClick={() => navigate(`/leads/${m.id}`)} data-testid={`view-existing-${m.id}`}>
                          <Eye className="h-4 w-4" /> View
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <div className="flex justify-end">
                <Button onClick={goToDetails} disabled={!canContinue} className="bg-teal hover:bg-teal-700 h-11" data-testid="continue-to-details-btn">
                  Continue <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {step === 2 && (
        <div className="bg-white border border-slate-200/80 rounded-xl shadow-sm p-6 space-y-5" data-testid="lead-details-step">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Lead type" required>
              <Select value={form.lead_type} onValueChange={(v) => setF("lead_type", v)}>
                <SelectTrigger data-testid="lead-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{(meta.lead_types || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Organization name" required><Input value={form.org_name} onChange={(e) => setF("org_name", e.target.value)} data-testid="lead-org-input" /></Field>
            <Field label="Contact person"><Input value={form.contact_person} onChange={(e) => setF("contact_person", e.target.value)} data-testid="lead-contact-input" /></Field>
            <Field label="Mobile number"><Input value={form.mobile} onChange={(e) => setF("mobile", e.target.value)} data-testid="lead-mobile-input" /></Field>
            <Field label="WhatsApp number"><Input value={form.whatsapp} onChange={(e) => setF("whatsapp", e.target.value)} /></Field>
            <Field label="Email"><Input value={form.email} onChange={(e) => setF("email", e.target.value)} /></Field>
            <Field label="City"><Input value={form.city} onChange={(e) => setF("city", e.target.value)} /></Field>
            <Field label="Address"><Input value={form.address} onChange={(e) => setF("address", e.target.value)} /></Field>
            <Field label="Website"><Input value={form.website} onChange={(e) => setF("website", e.target.value)} /></Field>
            <Field label="Lead source">
              <Select value={form.source} onValueChange={(v) => setF("source", v)}>
                <SelectTrigger data-testid="lead-source-select"><SelectValue /></SelectTrigger>
                <SelectContent>{(meta.lead_sources || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="No. of doctors/users"><Input type="number" value={form.num_doctors} onChange={(e) => setF("num_doctors", e.target.value)} /></Field>
            <Field label="No. of locations"><Input type="number" value={form.num_locations} onChange={(e) => setF("num_locations", e.target.value)} /></Field>
            <Field label="Estimated deal value (₹)"><Input type="number" value={form.estimated_value} onChange={(e) => setF("estimated_value", e.target.value)} data-testid="lead-value-input" /></Field>
            <Field label="Next follow-up date"><Input type="date" value={form.next_action_date} onChange={(e) => setF("next_action_date", e.target.value)} /></Field>
          </div>
          <Field label="Requirement"><Textarea rows={2} value={form.requirement} onChange={(e) => setF("requirement", e.target.value)} /></Field>
          <Field label="Next action" required><Input value={form.next_action} onChange={(e) => setF("next_action", e.target.value)} placeholder="e.g. Schedule a demo call" data-testid="lead-next-action-input" /></Field>
          <Field label="Notes"><Textarea rows={2} value={form.notes} onChange={(e) => setF("notes", e.target.value)} /></Field>
          <Field label="Private notes (owner only)"><Textarea rows={2} value={form.private_notes} onChange={(e) => setF("private_notes", e.target.value)} /></Field>

          <div className="flex justify-between pt-2">
            <Button variant="outline" onClick={() => setStep(1)} data-testid="back-to-step1-btn">Back</Button>
            <Button onClick={submit} disabled={saving} className="bg-teal hover:bg-teal-700 h-11" data-testid="create-lead-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create Lead"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
